/**
 * ui.js — hit feedback and interface tones.
 *
 * These are the only sounds in the game that are allowed to be synthetic on purpose.
 * They are information, not simulation, so they get clean pitched material with tight
 * envelopes and a deliberate pitch relationship between them:
 *
 *   ui_hit       one short tick               "you connected"
 *   ui_headshot  the same tick a fifth higher, plus a bright ping   "you connected well"
 *   ui_kill      a falling two-note figure with a tail              "that one is finished"
 *
 * Keeping them in one pitch family means a burst of hitmarkers during automatic fire
 * sounds like a rhythm rather than like noise.
 */

import {
  noiseBurst, shape, chain, gain, partial, modal, rand,
} from './dsp.js';

const ROOT = 1480; // F#6-ish; sits above weapon energy so it is never masked.

export function uiHit(ac, dest, t, rng) {
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 4000, Q: 0.7, level: 0.5, attack: 0.0003, decay: 0.004,
  });
  partial(ac, dest, t, ROOT, 0.55, 0.045, { attack: 0.0006 });
  partial(ac, dest, t, ROOT * 1.5, 0.3, 0.03, { attack: 0.0006 });
  partial(ac, dest, t, ROOT * 2.51, 0.12, 0.018, { attack: 0.0004 });
}

export function uiHeadshot(ac, dest, t, rng) {
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 5200, Q: 0.7, level: 0.55, attack: 0.0002, decay: 0.0035,
  });
  // Same interval structure as ui_hit, transposed up a fifth, with a small upward
  // glide on the top partial — the rise is what reads as "better than a normal hit".
  const f = ROOT * 1.5;
  partial(ac, dest, t, f, 0.6, 0.06, { attack: 0.0005 });
  partial(ac, dest, t, f * 1.5, 0.34, 0.05, { attack: 0.0005, glideTo: f * 1.62 });
  partial(ac, dest, t, f * 2.51, 0.16, 0.03, { attack: 0.0004 });
  partial(ac, dest, t + 0.035, f * 2.0, 0.22, 0.13, { attack: 0.001, glideTo: f * 2.08 });
}

export function uiKill(ac, dest, t, rng) {
  // Falling minor third, metallic, with a short ring — final and slightly cold.
  const a = ROOT * 0.75;
  const b = a * 0.8409; // -3 semitones
  modal(ac, dest, t, a, [
    [1.0, 0.5, 0.11],
    [2.02, 0.22, 0.08],
    [3.41, 0.1, 0.05],
  ], { spread: 0.02, rng });
  modal(ac, dest, t + 0.075, b, [
    [1.0, 0.45, 0.3],
    [2.02, 0.2, 0.2],
    [3.41, 0.09, 0.12],
    [5.11, 0.05, 0.08],
  ], { spread: 0.02, rng });
  noiseBurst(ac, dest, t, rng, {
    filter: 'highpass', freq: 4600, Q: 0.7, level: 0.3, attack: 0.0003, decay: 0.005,
  });
  // Low seal underneath.
  partial(ac, dest, t + 0.075, 150, 0.28, 0.16, { glideTo: 105, attack: 0.002 });
}

export function uiSelect(ac, dest, t, rng) {
  // Soft, dull, short. UI navigation must never compete with combat feedback.
  const f = rand(rng, 860, 940);
  partial(ac, dest, t, f, 0.4, 0.045, { attack: 0.002, type: 'triangle' });
  partial(ac, dest, t, f * 2.0, 0.12, 0.025, { attack: 0.002 });
  noiseBurst(ac, dest, t, rng, {
    filter: 'bandpass', freq: 2600, Q: 1.2, level: 0.12, attack: 0.001, decay: 0.012,
  });
}

/** Low-health / suppression whine. Also used as the tinnitus source after a blast. */
export function tinnitusTone(ac, dest, t, dur = 2.0, level = 0.05) {
  const g = shape(ac, t, [[0, 0], [0.02, level], [dur * 0.35, level * 0.6], [dur, 1e-5]]);
  g.connect(dest);
  for (const [f, lv] of [[4680, 1], [6270, 0.45], [8890, 0.2]]) {
    const o = ac.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f, t);
    o.frequency.linearRampToValueAtTime(f * 0.97, t + dur);
    chain(o, gain(ac, lv), g);
    o.start(t); o.stop(t + dur + 0.05);
  }
  return g;
}
